import React from 'react';
import axios from 'axios';
import { BASE_URL } from '../../../global/Server/endpoints';
import FeedbackBar from '../../../feedbackBar/FeedbackBar';
import { userInfoSelector } from '../../../../redux/selectors/auth';
import withShipment from '../../../../withShipment';
import './ATMReceipt.css';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormGroup,
  Grid,
  TextField,
} from '@material-ui/core';
import CustomToolbar from '../../../global/CustomToolbar';
import { addDays, endOfMonth, format, startOfMonth, subDays } from 'date-fns';
import FolderCard from '../liz/FolderCard';
import { MONTHS } from './constant';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Button, FormControlLabel } from '@mui/material';
import RefreshIcon from '@material-ui/icons/Refresh';
import { v4 as uuidv4 } from 'uuid';

class ATMReceipt extends React.Component {
  constructor(props) {
    super(props);

    const startYear = 2025;
    const currentYear = new Date().getFullYear();

    const YEARS = [];
    for (let year = startYear; year <= currentYear; year++) {
      YEARS.push(year);
    }

    this.state = {
      YEARS,
      locations: [],
      profitSummary: [],
      feedbackBarOpen: false,
      feedbackStatus: '',
      feedbackMessage: '',
      selectedYear: null,
      selectedMonth: null,
      selectedWeek: null,
      atmSummary: null,
      autoRows: [],
      lastWeekRows: [],
      manualNextWeekRows: [],
      originalManualRows: [],
      saving: false,
      showATM: true,
      showLastWeek: true,
      showNextWeek: true,
      showDidNotUse: true,

      confirmRefreshOpen: false,
      refreshing: false,
    };
  }

  async componentDidMount() {
    await this.fetchLocations();

    const result = this.getLastCompletedAccountingWeek();

    if (!result) return;

    const { year, monthIndex, week } = result;

    this.setState(
      {
        selectedYear: year,
        selectedMonth: monthIndex,
      },
      async () => {
        await this.openWeek(week);
      }
    );
  }

  openRefreshConfirm = () => {
    this.setState({ confirmRefreshOpen: true });
  };

  refreshFromManagerSummary = async () => {
    const { selectedWeek, locations, autoRows } = this.state;

    if (!selectedWeek) {
      this.openFeedback('error', 'Please select a week before refreshing');
      return;
    }

    if (!locations.length) {
      this.openFeedback('error', 'No locations available to fetch summary');
      return;
    }

    this.setState({ refreshing: true });

    try {
      const from = format(selectedWeek.fromDate, 'yyyy-MM-dd');
      const to = format(selectedWeek.toDate, 'yyyy-MM-dd');

      const res = await axios.post(
        `${BASE_URL}/lizAccounting/locationSummary`,
        {
          fromDate: from,
          toDate: to,
          locations: locations.map((l) => l.name),
        },
        { headers: { Authorization: `Bearer ${this.getToken()}` } }
      );

      if (!res.data.success) {
        this.openFeedback('error', 'Failed to fetch ATM summary');
        return;
      }

      // Map API results to structured summaries
      const atmSummaries = res.data.sessions
        .map((session) => {
          const loc = locations.find(
            (l) => l.name.toUpperCase() === session.locationName?.toUpperCase()
          );
          if (!loc) return null;
          return { location: loc.name, summary: session.managerSummary };
        })
        .filter(Boolean);

      // Prepare new rows from summaries
      const newAutoRows = Object.entries(
        atmSummaries.reduce((acc, { summary }) => {
          const manager = summary.managerName?.trim();
          if (!manager) return acc;

          if (!acc[manager])
            acc[manager] = {
              id: uuidv4(), // generate a new id here
              manager,
              eric: 0,
              profit: 0,
              atmDidntUse: 0,
              advEly: 0,
              isEditing: false,
            };

          acc[manager].eric += this.getAmount(
            summary.atmTransactions,
            'ATM_CASH'
          );
          acc[manager].profit += this.getAmount(
            summary.atmTransactions,
            'ATM_ADV'
          );
          acc[manager].atmDidntUse += this.getAmount(
            summary.atmTransactions,
            'ATM_DIDNT_USE'
          );

          return acc;
        }, {})
      ).map(([_, row]) => row);

      // Merge with existing autoRows
      const mergedAutoRows = autoRows.map((r) => ({ ...r }));

      newAutoRows.forEach((newRow) => {
        const index = mergedAutoRows.findIndex(
          (r) => r.manager === newRow.manager && !r.isEditing
        );

        if (index !== -1) {
          mergedAutoRows[index] = {
            ...mergedAutoRows[index],
            eric: newRow.eric,
            profit: newRow.profit,
            atmDidntUse: newRow.atmDidntUse,
            advEly: newRow.advEly,
          };
        } else {
          mergedAutoRows.push({ ...newRow });
        }
      });

      // Update state
      this.setState(
        {
          atmSummary: atmSummaries,
          autoRows: mergedAutoRows,
        },
        this.initLastWeekRows
      );

      this.openFeedback('success', 'ATM values refreshed successfully');
    } catch (err) {
      this.openFeedback('error', `Failed to fetch ATM summary: ${err.message}`);
    } finally {
      this.setState({ refreshing: false, confirmRefreshOpen: false });
    }
  };

  getLastCompletedAccountingWeek = () => {
    const today = new Date();

    // Check current month first
    let year = today.getFullYear();
    let monthIndex = today.getMonth();

    for (let i = 0; i < 12; i++) {
      const weeks = this.generateWeeksForMonth(year, monthIndex);

      const lastWeek = [...weeks].reverse().find((w) => w.toDate < today);

      if (lastWeek) {
        return { year, monthIndex, week: lastWeek };
      }

      // Move to previous month
      monthIndex--;
      if (monthIndex < 0) {
        monthIndex = 11;
        year--;
      }
    }

    return null;
  };

  initAutoRows = () => {
    const autoRows = this.getNextWeekAutoRows().map((r) => ({
      ...r,
      isEditing: false, // start in display mode
    }));
    this.setState({ autoRows });
  };

  toggleSection = (key) => {
    this.setState((prev) => ({ [key]: !prev[key] }));
  };

  sumBy = (arr, fn) => arr.reduce((t, x) => t + fn(x), 0);

  getAmount = (txs, type) =>
    this.sumBy(txs?.filter((t) => t.type === type) || [], (t) => t.amount || 0);

  groupByManager = () => {
    const map = {};
    (this.state.atmSummary || []).forEach(({ summary }) => {
      const name = summary.managerName?.trim();
      if (!name || name === 'undefined' || name === 'null') return;
      if (!map[name]) map[name] = [];
      map[name].push(summary);
    });
    return map;
  };

  getATMOverviewRows = () => {
    return (this.state.atmSummary || []).map(({ location, summary }) => {
      const fromEric = this.getAmount(summary.atmTransactions, 'ATM_CASH');
      const fromProfit = this.getAmount(
        summary.atmTransactions,
        'ATM_FROM_PROFIT'
      );

      return {
        location,
        fromEric,
        fromProfit,
        total: fromEric + fromProfit,
      };
    });
  };

  getDidNotUseRows = () => {
    return (this.state.atmSummary || [])
      .map(({ location, summary }) => ({
        id: `${summary.managerName}-${location}`,
        location,
        manager: summary.managerName,
        amount: this.getAmount(summary.atmTransactions, 'ATM_DIDNT_USE'),
      }))
      .filter(
        (row) =>
          row.amount > 0 &&
          row.manager &&
          row.manager !== 'undefined' &&
          row.manager !== 'null'
      );
  };

  initLastWeekRows = () => {
    const grouped = this.groupByManager();
    const existing = this.state.lastWeekRows;

    const rows = Object.entries(grouped)
      .filter(
        ([manager]) => manager && manager !== 'undefined' && manager !== 'null'
      )
      .map(([manager, summaries]) => {
        const atmLastWeek = this.sumBy(summaries, (s) =>
          Number(s.totalATMCash || 0)
        );
        // Preserve any previously edited midWeekAdv value
        const existingRow = existing.find((r) => r.manager === manager);
        const midWeekAdv = existingRow ? existingRow.midWeekAdv : 0;
        return {
          manager,
          atmLastWeek,
          midWeekAdv,
          total: atmLastWeek + midWeekAdv,
          isEditing: false,
        };
      });

    this.setState({ lastWeekRows: rows });
  };

  getLastWeekRows = () => {
    return this.state.lastWeekRows;
  };

  toggleLastWeekRowEdit = (manager) => {
    this.setState((prev) => ({
      lastWeekRows: prev.lastWeekRows.map((r) =>
        r.manager === manager ? { ...r, isEditing: !r.isEditing } : r
      ),
    }));
  };

  updateLastWeekRow = (manager, value) => {
    const midWeekAdv = value === '' ? 0 : Number(value);
    this.setState((prev) => ({
      lastWeekRows: prev.lastWeekRows.map((r) =>
        r.manager === manager
          ? { ...r, midWeekAdv, total: r.atmLastWeek + midWeekAdv }
          : r
      ),
    }));
  };

  getNextWeekAutoRows = () => {
    const grouped = this.groupByManager();

    return Object.entries(grouped)
      .filter(
        ([manager]) => manager && manager !== 'undefined' && manager !== 'null'
      )
      .map(([manager, summaries]) => {
        const eric = this.sumBy(summaries, (s) =>
          this.getAmount(s.atmTransactions, 'ATM_CASH')
        );

        const profit = this.sumBy(summaries, (s) =>
          this.getAmount(s.atmTransactions, 'ATM_ADV')
        );

        const atmDidntUse = this.sumBy(summaries, (s) =>
          this.getAmount(s.atmTransactions, 'ATM_DIDNT_USE')
        );

        return {
          id: uuidv4(),
          manager,
          eric,
          profit,
          atmDidntUse,
          advEly: 0,
          total: eric + profit + atmDidntUse,
          isEditing: false,
        };
      });
  };

  toggleAutoRowEdit = (id) => {
    this.setState({
      autoRows: this.state.autoRows.map((r) =>
        r.id === id ? { ...r, isEditing: !r.isEditing } : r
      ),
    });
  };

  updateAutoRow = (id, field, value) => {
    this.setState({
      autoRows: this.state.autoRows.map((r) =>
        r.id === id
          ? {
              ...r,
              [field]:
                field === 'manager' ? value : value === '' ? '' : Number(value),
            }
          : r
      ),
    });
  };

  addManualRow = () => {
    this.setState({
      manualNextWeekRows: [
        ...this.state.manualNextWeekRows,
        {
          id: uuidv4(),
          manager: '',
          eric: 0,
          profit: 0,
          atmDidntUse: 0,
          advEly: 0,
          isEditing: true, // new row starts in edit mode
        },
      ],
    });
  };

  updateManualRow = (id, field, value) => {
    this.setState((prev) => ({
      manualNextWeekRows: prev.manualNextWeekRows.map((r) =>
        r.id === id
          ? {
              ...r,
              [field]:
                field === 'manager' ? value : value === '' ? '' : Number(value),
            }
          : r
      ),
    }));
  };

  toggleEditRow = (id) => {
    this.setState({
      manualNextWeekRows: this.state.manualNextWeekRows.map((r) =>
        r.id === id ? { ...r, isEditing: !r.isEditing } : r
      ),
    });
  };

  getManualTotal = () => {
    const allRows = [
      ...(this.state.autoRows || []),
      ...this.state.manualNextWeekRows,
    ];
    const eric = allRows.reduce((sum, r) => sum + (r.eric || 0), 0);
    const profit = allRows.reduce((sum, r) => sum + (r.profit || 0), 0);
    const atmDidntUse = allRows.reduce(
      (sum, r) => sum + (r.atmDidntUse || 0),
      0
    );
    const advEly = allRows.reduce((sum, r) => sum + (r.advEly || 0), 0);

    return {
      eric,
      profit,
      atmDidntUse,
      advEly,
      total: eric + profit + atmDidntUse + advEly,
    };
  };

  removeManualRow = (id) => {
    this.setState({
      manualNextWeekRows: this.state.manualNextWeekRows.filter(
        (r) => r.id !== id
      ),
    });
  };

  saveWeek = async () => {
    const { selectedYear, selectedMonth, selectedWeek } = this.state;
    this.setState({ saving: true });
    try {
      const payload = {
        year: selectedYear,
        month: selectedMonth + 1,
        weekNumber: selectedWeek.weekNumber,
        fromDate: selectedWeek.fromDate,
        toDate: selectedWeek.toDate,

        atmSummary: this.state.atmSummary,
        lastWeekRows: this.state.lastWeekRows.map((r) => ({
          manager: r.manager,
          atmLastWeek: r.atmLastWeek,
          midWeekAdv: r.midWeekAdv,
        })),
        autoRows: this.state.autoRows.map((r) => ({
          id: r.id,
          manager: r.manager,
          eric: r.eric,
          profit: r.profit,
          advEly: r.advEly,
          atmDidntUse: r.atmDidntUse,
        })),
        manualNextWeekRows: this.state.manualNextWeekRows.map((r) => ({
          id: r.id,
          manager: r.manager,
          eric: r.eric,
          profit: r.profit,
          advEly: r.advEly,
          atmDidntUse: r.atmDidntUse,
        })),
      };

      await axios.post(`${BASE_URL}/lizAccounting/saveATMWeekData`, payload, {
        headers: { Authorization: `Bearer ${this.getToken()}` },
      });

      this.setState({
        originalManualRows: [...this.state.manualNextWeekRows],
        saving: false,
      });

      this.openFeedback('success', 'Week saved successfully');
    } catch (e) {
      this.openFeedback('error', 'Save failed');
      this.setState({ saving: false });
    }
  };

  getToken = () => localStorage.getItem('token');

  fetchLocations = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/location/list`, {
        headers: { Authorization: `Bearer ${this.getToken()}` },
      });
      if (res.data.success) {
        this.setState({ locations: res.data.locations }, () => {});
      } else {
        this.openFeedback('error', 'Failed to fetch locations');
      }
    } catch (err) {
      this.openFeedback('error', `Failed to fetch locations: ${err.message}`);
    }
  };

  generateWeeksForMonth = (year, monthIndex) => {
    const weeks = [];
    let date = startOfMonth(new Date(year, monthIndex));

    // move to first Tuesday (Feb 3 for Feb 2026)
    while (date.getDay() !== 2) {
      date = addDays(date, 1);
    }

    let week = 1;
    while (date <= endOfMonth(new Date(year, monthIndex))) {
      const fromDate = new Date(date); // This remains Feb 3
      const toDate = addDays(fromDate, 6); // This remains Feb 9

      weeks.push({
        weekNumber: week,
        fromDate,
        toDate,
        // We subtract 1 day ONLY for the display label
        label: `Week ${week} (${format(
          addDays(fromDate, -1),
          'MMM d'
        )}–${format(addDays(toDate, -1), 'd')})`,
      });

      week++;
      date = addDays(date, 7);
    }

    return weeks;
  };

  openYear = (year) => {
    this.setState({
      selectedYear: year,
      selectedMonth: null,
      selectedWeek: null,
    });
  };

  openMonth = (monthIndex) => {
    this.setState({
      selectedMonth: monthIndex,
      selectedWeek: null,
    });
  };

  openWeek = async (week) => {
    const { selectedYear, selectedMonth, locations } = this.state;

    this.setState({
      selectedWeek: week,
      fromDate: week.fromDate,
      toDate: week.toDate,

      atmSummary: [],
      autoRows: [],
      lastWeekRows: [],
      manualNextWeekRows: [],
      originalManualRows: [],
    });

    try {
      // 1️⃣ Fetch saved week from backend
      const res = await axios.post(
        `${BASE_URL}/lizAccounting/getATMWeekData`,
        {
          year: selectedYear,
          month: selectedMonth + 1, // monthIndex 0-11 → 1-12
          weekNumber: week.weekNumber,
        },
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        }
      );

      if (res.data.success && res.data.week) {
        const weekData = res.data.week;

        this.setState({
          ...weekData,
          lastWeekRows: (weekData.lastWeekRows || []).map((r) => ({
            ...r,
            total: r.atmLastWeek + (r.midWeekAdv || 0),
            isEditing: false,
          })),
          autoRows: (weekData.autoRows || []).map((r) => ({
            ...r,
            id: r.id || uuidv4(), // 🔒 GUARANTEE STABLE ID
            isEditing: false,
          })),
          manualNextWeekRows: (weekData.manualNextWeekRows || []).map((r) => ({
            ...r,
            id: r.id || uuidv4(),
            isEditing: false,
          })),
        });
        return;
      }

      // 2️⃣ No saved data → fallback to fetch from locations
      if (locations.length) {
        await this.fetchManagerSummary();
      }
    } catch (err) {
      this.openFeedback('error', `Failed to load week data: ${err.message}`);
    }
  };

  fetchManagerSummary = async () => {
    const { fromDate, toDate, locations } = this.state;
    if (!locations.length) return;

    const from = format(fromDate, 'yyyy-MM-dd');
    const to = format(toDate, 'yyyy-MM-dd');

    try {
      const res = await axios.post(
        `${BASE_URL}/lizAccounting/locationSummary`,
        {
          fromDate: from,
          toDate: to,
          locations: locations.map((l) => l.name),
        },
        {
          headers: { Authorization: `Bearer ${this.getToken()}` },
        }
      );

      if (res.data.success) {
        // Collect summaries per location
        const atmSummaries = res.data.sessions
          .map((session) => {
            const loc = locations.find(
              (l) => l.name.toUpperCase() === session.locationName.toUpperCase()
            );
            if (!loc) return null;

            return {
              location: loc.name,
              summary: session.managerSummary,
            };
          })
          .filter(Boolean); // remove nulls

        // Set state once
        this.setState({ atmSummary: atmSummaries }, () => {
          this.initAutoRows();
          this.initLastWeekRows();
        });
      } else {
        this.openFeedback('error', 'Failed to fetch ATM summary');
      }
    } catch (err) {
      this.openFeedback('error', `Failed to fetch ATM summary: ${err.message}`);
    }
  };

  openFeedback = (severity, message) => {
    this.setState({
      feedbackBarOpen: true,
      feedbackStatus: severity,
      feedbackMessage: message,
    });
  };

  closeFeedbackBar = () => {
    this.setState({ feedbackBarOpen: false });
  };

  render() {
    let headerButtons = [];

    if (
      this.state.selectedWeek ||
      this.state.selectedMonth ||
      this.state.selectedYear
    ) {
      headerButtons.push({
        icon: <KeyboardBackspaceIcon />,
        title: 'Back',
        handler: () => {
          if (this.state.selectedWeek) {
            this.setState({ selectedWeek: null });
          } else if (this.state.selectedMonth !== null) {
            this.setState({ selectedMonth: null });
          } else if (this.state.selectedYear !== null) {
            this.setState({ selectedYear: null });
          }
        },
      });

      if (this.state.selectedWeek) {
        headerButtons.push({
          icon: <RefreshIcon />,
          title: 'Refresh',
          handler: this.openRefreshConfirm,
        });
      }
    }

    const didNotUseRows = this.getDidNotUseRows();

    const totalDidNotUse = this.sumBy(didNotUseRows, (r) => r.amount);

    const lastWeekRows = this.getLastWeekRows();

    const lastWeekTotal = this.sumBy(lastWeekRows, (r) => r.atmLastWeek);

    const totalUsed = lastWeekTotal - totalDidNotUse;

    const atmRows = this.getATMOverviewRows();

    const atmTotals = atmRows.reduce(
      (acc, r) => {
        acc.fromEric += r.fromEric;
        acc.fromProfit += r.fromProfit;
        acc.total += r.total;
        return acc;
      },
      { fromEric: 0, fromProfit: 0, total: 0 }
    );

    const lastWeekGrandTotal = lastWeekRows.reduce(
      (sum, r) => sum + r.total,
      0
    );

    const lastWeekMidWeekAdvTotal = lastWeekRows.reduce(
      (sum, r) => sum + r.midWeekAdv,
      0
    );

    const lastWeekAtmTotal = lastWeekRows.reduce(
      (sum, r) => sum + r.atmLastWeek,
      0
    );

    return (
      <div>
        <CustomToolbar
          title={
            this.state.selectedWeek
              ? `Week of ${format(
                  subDays(this.state.selectedWeek.fromDate, 1),
                  'MMM d, yyyy'
                )}`
              : this.state.selectedMonth !== null
              ? `${MONTHS[this.state.selectedMonth]} ${this.state.selectedYear}`
              : this.state.selectedYear !== null
              ? `Year ${this.state.selectedYear}`
              : 'ATM'
          }
          headerButtons={headerButtons}
        />

        <FeedbackBar
          open={this.state.feedbackBarOpen}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
          handleClose={this.closeFeedbackBar}
        />

        <div className="root">
          <Dialog
            open={this.state.confirmRefreshOpen}
            onClose={() => this.setState({ confirmRefreshOpen: false })}
            PaperProps={{
              sx: { borderRadius: 3, padding: 2, minWidth: 400 }, // rounded corners & padding
            }}
          >
            <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
              Refresh ATM Records
            </DialogTitle>

            <DialogContent dividers>
              <DialogContentText
                sx={{ mb: 2, fontSize: '0.95rem', color: 'text.secondary' }}
              >
                This action will refresh all records from the Manager Summary.
              </DialogContentText>
              <DialogContentText sx={{ fontWeight: 'medium', mb: 1 }}>
                Only manual entries will <b>remain unchanged</b>.
              </DialogContentText>
              <DialogContentText sx={{ color: 'text.primary' }}>
                Are you sure you want to continue?
              </DialogContentText>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center', gap: 2, mb: 1 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => this.setState({ confirmRefreshOpen: false })}
                sx={{ minWidth: 120 }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={this.refreshFromManagerSummary}
                disabled={this.state.refreshing}
                sx={{ minWidth: 120 }}
              >
                {this.state.refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </DialogActions>
          </Dialog>

          {!this.state.selectedYear && (
            <Grid container spacing={4}>
              {this.state.YEARS.map((year) => (
                <Grid item xs={12} sm={6} md={3} key={year}>
                  <FolderCard
                    title={year}
                    subtitle="Accounting Year"
                    variant="year"
                    onClick={() => this.openYear(year)}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {this.state.selectedYear !== null &&
            this.state.selectedMonth === null && (
              <Grid container spacing={3}>
                {MONTHS.map((month, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={month}>
                    <FolderCard
                      title={month}
                      subtitle="Accounting Month"
                      variant="month"
                      onClick={() => this.openMonth(idx)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

          {this.state.selectedYear !== null &&
            this.state.selectedMonth !== null &&
            !this.state.selectedWeek && (
              <Grid container spacing={3}>
                {this.generateWeeksForMonth(
                  this.state.selectedYear,
                  this.state.selectedMonth
                ).map((week) => (
                  <Grid item xs={12} sm={6} md={4} key={week.weekNumber}>
                    <FolderCard
                      title={week.label}
                      subtitle="Accounting Week"
                      variant="week"
                      onClick={() => this.openWeek(week)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

          {this.state.selectedWeek && (
            <div className="atm-wrapper">
              <div className="atm-filters">
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.showATM}
                        onChange={() => this.toggleSection('showATM')}
                        color="primary"
                      />
                    }
                    label="ATM Summary"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.showLastWeek}
                        onChange={() => this.toggleSection('showLastWeek')}
                        color="primary"
                      />
                    }
                    label="ATM Last Week"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.showNextWeek}
                        onChange={() => this.toggleSection('showNextWeek')}
                        color="primary"
                      />
                    }
                    label="ATM Next Week"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.showDidNotUse}
                        onChange={() => this.toggleSection('showDidNotUse')}
                        color="primary"
                      />
                    }
                    label="ATM Didn't Use"
                  />
                </FormGroup>
              </div>

              {/* TABLE 1 */}
              {this.state.showATM && (
                <div className="atm-card">
                  <h3>ATM Summary</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="atm-table">
                      <thead>
                        <tr>
                          <th className="header">ATM</th>
                          <th className="amount-header">ATM Cash</th>
                          <th className="amount-header">GM Profit</th>
                          <th className="amount-header">Total</th>
                        </tr>
                      </thead>

                      <tbody>
                        {atmRows.map((r) => (
                          <tr key={r.location}>
                            <td>{r.location}</td>
                            <td className="amount-cell">
                              ${r.fromEric.toLocaleString()}
                            </td>
                            <td className="amount-cell">
                              ${r.fromProfit.toLocaleString()}
                            </td>
                            <td className="amount-cell">
                              ${r.total.toLocaleString()}
                            </td>
                          </tr>
                        ))}

                        {/* TOTAL ROW */}
                        <tr className="summary-row">
                          <td className="bold">TOTAL</td>
                          <td className="amount-cell bold">
                            ${atmTotals.fromEric.toLocaleString()}
                          </td>
                          <td className="amount-cell bold">
                            ${atmTotals.fromProfit.toLocaleString()}
                          </td>
                          <td className="amount-cell bold">
                            ${atmTotals.total.toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ATM LAST WEEK - updated with Mid Week ADV column */}
              {this.state.showLastWeek && (
                <div className="atm-card">
                  <h3>ATM Last Week</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="atm-table">
                      <thead>
                        <tr>
                          <th style={{ width: '40px', padding: '0 4px' }} />
                          <th
                            className="header"
                            style={{ textAlign: 'left', paddingLeft: '8px' }}
                          >
                            Manager
                          </th>
                          <th className="amount-header">ATM Last Week</th>
                          <th className="amount-header">Mid Week ADV</th>
                          <th className="amount-header">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lastWeekRows.map((r) => (
                          <tr key={`last-${r.manager}`}>
                            <td
                              style={{
                                width: '40px',
                                padding: '0 4px',
                                textAlign: 'center',
                              }}
                            >
                              <button
                                className="icon-btn"
                                onClick={() =>
                                  this.toggleLastWeekRowEdit(r.manager)
                                }
                              >
                                {r.isEditing ? (
                                  <CheckCircleIcon color="success" />
                                ) : (
                                  <EditIcon color="primary" />
                                )}
                              </button>
                            </td>
                            <td
                              style={{ textAlign: 'left', paddingLeft: '8px' }}
                            >
                              {r.manager}
                            </td>
                            <td className="amount-cell">
                              ${r.atmLastWeek.toLocaleString()}
                            </td>
                            <td className="amount-cell">
                              {r.isEditing ? (
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  type="number"
                                  value={r.midWeekAdv}
                                  onChange={(e) =>
                                    this.updateLastWeekRow(
                                      r.manager,
                                      e.target.value
                                    )
                                  }
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        $
                                      </InputAdornment>
                                    ),
                                    inputProps: {
                                      style: {
                                        textAlign: 'right',
                                        width: '80px',
                                      },
                                    },
                                  }}
                                />
                              ) : (
                                `$${r.midWeekAdv.toLocaleString()}`
                              )}
                            </td>
                            <td className="amount-cell">
                              ${r.total.toLocaleString()}
                            </td>
                          </tr>
                        ))}

                        {/* TOTAL ROW */}
                        <tr className="summary-row">
                          <td style={{ width: '40px', padding: '0 4px' }} />
                          <td
                            className="bold"
                            style={{ textAlign: 'left', paddingLeft: '8px' }}
                          >
                            TOTAL
                          </td>
                          <td className="amount-cell bold">
                            ${lastWeekAtmTotal.toLocaleString()}
                          </td>
                          <td className="amount-cell bold">
                            ${lastWeekMidWeekAdvTotal.toLocaleString()}
                          </td>
                          <td className="amount-cell bold">
                            ${lastWeekGrandTotal.toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {this.state.showDidNotUse && (
                <div className="atm-card warning">
                  <h3>ATM Didn't Use</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="atm-table">
                      <tbody>
                        {didNotUseRows.map((r) => (
                          <tr key={r.id}>
                            <td>{r.manager}</td>
                            <td className="amount-cell">
                              ${r.amount.toLocaleString()}
                            </td>
                          </tr>
                        ))}

                        {/* TOTAL DID NOT USE */}
                        <tr className="summary-row">
                          <td className="bold">Total Didn't Use</td>
                          <td className="amount-cell bold">
                            ${totalDidNotUse.toLocaleString()}
                          </td>
                        </tr>

                        {/* LAST WEEK */}
                        <tr className="summary-row">
                          <td className="bold">Last Week</td>
                          <td className="amount-cell bold">
                            ${lastWeekTotal.toLocaleString()}
                          </td>
                        </tr>

                        {/* TOTAL USE */}
                        <tr className="summary-row">
                          <td className="bold">Total Use</td>
                          <td className="amount-cell bold">
                            ${totalUsed.toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* TABLE 4 - ATM Next Week (Mid Week ADV removed) */}

              {this.state.showNextWeek && (
                <div className="atm-card highlight">
                  <h3>ATM Next Week</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="atm-table">
                      <thead>
                        <tr>
                          <th />
                          <th className="header">Manager</th>
                          <th className="amount-header">ATM Cash</th>
                          <th className="amount-header">ATM ADV</th>
                          <th className="amount-header">ATM Didn't Use</th>
                          <th className="amount-header">Total</th>
                        </tr>
                      </thead>

                      <tbody>
                        {/* Auto rows */}
                        {this.state.autoRows?.map((r) => {
                          return (
                            <tr key={`auto-${r.id}`} className="readonly">
                              <td>
                                <button
                                  className="icon-btn"
                                  onClick={() => this.toggleAutoRowEdit(r.id)}
                                >
                                  {r.isEditing ? (
                                    <CheckCircleIcon color="success" />
                                  ) : (
                                    <EditIcon color="primary" />
                                  )}
                                </button>
                              </td>

                              <td className="header">
                                {r.isEditing ? (
                                  <TextField
                                    className="responsive-field"
                                    variant="outlined"
                                    size="small"
                                    type="text"
                                    value={r.manager}
                                    onChange={(e) =>
                                      this.updateAutoRow(
                                        r.id,
                                        'manager',
                                        e.target.value
                                      )
                                    }
                                  />
                                ) : (
                                  `${r.manager}`
                                )}
                              </td>

                              <td>
                                {r.isEditing ? (
                                  <TextField
                                    className="responsive-field"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={r.eric}
                                    onChange={(e) =>
                                      this.updateAutoRow(
                                        r.id,
                                        'eric',
                                        e.target.value
                                      )
                                    }
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          $
                                        </InputAdornment>
                                      ),
                                      inputProps: {
                                        style: { textAlign: 'right' },
                                      },
                                    }}
                                  />
                                ) : (
                                  `$${r.eric.toLocaleString()}`
                                )}
                              </td>

                              <td>${r.profit.toLocaleString()}</td>

                              <td>
                                {r.isEditing ? (
                                  <TextField
                                    variant="outlined"
                                    className="responsive-field"
                                    size="small"
                                    type="number"
                                    value={r.atmDidntUse}
                                    onChange={(e) =>
                                      this.updateAutoRow(
                                        r.id,
                                        'atmDidntUse',
                                        e.target.value
                                      )
                                    }
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          $
                                        </InputAdornment>
                                      ),
                                      inputProps: {
                                        style: { textAlign: 'right' },
                                      },
                                    }}
                                  />
                                ) : (
                                  `$${r.atmDidntUse.toLocaleString()}`
                                )}
                              </td>

                              <td>
                                $
                                {(
                                  r.eric +
                                  r.profit +
                                  r.atmDidntUse
                                ).toLocaleString()}
                              </td>
                            </tr>
                          );
                        })}

                        {/* Manual rows */}
                        {this.state.manualNextWeekRows.map((r) => (
                          <tr key={r.id} className="manual">
                            {r.isEditing ? (
                              <>
                                <td>
                                  <button
                                    className="icon-btn"
                                    onClick={() => this.removeManualRow(r.id)}
                                  >
                                    <RemoveCircleIcon color="error" />
                                  </button>
                                  <button
                                    className="icon-btn"
                                    onClick={() => this.toggleEditRow(r.id)}
                                  >
                                    <CheckCircleIcon color="success" />
                                  </button>
                                </td>

                                <td className="header">
                                  <TextField
                                    variant="outlined"
                                    className="responsive-field"
                                    size="small"
                                    type="text"
                                    value={r.manager}
                                    onChange={(e) =>
                                      this.updateManualRow(
                                        r.id,
                                        'manager',
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>

                                <td>
                                  <TextField
                                    variant="outlined"
                                    className="responsive-field"
                                    size="small"
                                    type="number"
                                    value={r.eric}
                                    onChange={(e) =>
                                      this.updateManualRow(
                                        r.id,
                                        'eric',
                                        e.target.value
                                      )
                                    }
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          $
                                        </InputAdornment>
                                      ),
                                      inputProps: {
                                        style: { textAlign: 'right' },
                                      },
                                    }}
                                  />
                                </td>
                                <td>
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    className="responsive-field"
                                    type="number"
                                    value={r.profit}
                                    onChange={(e) =>
                                      this.updateManualRow(
                                        r.id,
                                        'profit',
                                        e.target.value
                                      )
                                    }
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          $
                                        </InputAdornment>
                                      ),
                                      inputProps: {
                                        style: { textAlign: 'right' },
                                      },
                                    }}
                                  />
                                </td>

                                <td>
                                  <TextField
                                    variant="outlined"
                                    className="responsive-field"
                                    size="small"
                                    type="number"
                                    value={r.atmDidntUse}
                                    onChange={(e) =>
                                      this.updateManualRow(
                                        r.id,
                                        'atmDidntUse',
                                        e.target.value
                                      )
                                    }
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          $
                                        </InputAdornment>
                                      ),
                                      inputProps: {
                                        style: { textAlign: 'right' },
                                      },
                                    }}
                                  />
                                </td>

                                <td>
                                  $
                                  {(
                                    r.eric +
                                    r.profit +
                                    r.atmDidntUse
                                  ).toLocaleString()}
                                </td>
                              </>
                            ) : (
                              <>
                                <td>
                                  <button
                                    className="icon-btn"
                                    onClick={() => this.toggleEditRow(r.id)}
                                  >
                                    <EditIcon color="primary" />
                                  </button>
                                </td>
                                <td className="header">{r.manager}</td>
                                <td>${r.eric.toLocaleString()}</td>
                                <td>${r.profit.toLocaleString()}</td>
                                <td>${r.atmDidntUse.toLocaleString()}</td>
                                <td>
                                  $
                                  {(
                                    r.eric +
                                    r.profit +
                                    r.atmDidntUse
                                  ).toLocaleString()}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}

                        {/* Total row */}
                        <tr className="total-row">
                          <td />
                          <td className="header">
                            <b>Total</b>
                          </td>
                          <td>
                            <b>
                              ${this.getManualTotal().eric.toLocaleString()}
                            </b>
                          </td>
                          <td>
                            <b>
                              ${this.getManualTotal().profit.toLocaleString()}
                            </b>
                          </td>
                          <td>
                            <b>
                              $
                              {this.getManualTotal().atmDidntUse.toLocaleString()}
                            </b>
                          </td>
                          <td>
                            <b>
                              ${this.getManualTotal().total.toLocaleString()}
                            </b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="atm-actions">
                    <button style={{ visibility: 'hidden' }} />
                    <button onClick={this.addManualRow}>+ Add Row</button>
                    <button className="primary" onClick={this.saveWeek}>
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ userInfo: userInfoSelector(state) });

export default withShipment(
  {
    mapStateToProps,
  },
  ATMReceipt
);
